// Routing Decision drill-down modal — read-only audit detail.
// Bridges member-facing transparency to examiner-grade traceability.

import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, Copy, CheckCircle2, AlertCircle, Loader2, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RAIL_STYLE = {
  fednow:            { label: "FedNow",               text: "text-teal-700",    border: "border-teal-500/40",    bg: "bg-teal-50",    dot: "bg-teal-500" },
  rtp:               { label: "RTP",                  text: "text-blue-700",    border: "border-blue-500/40",    bg: "bg-blue-50",    dot: "bg-blue-500" },
  stablecoin:        { label: "Stablecoin Express",   text: "text-purple-700",  border: "border-purple-500/40",  bg: "bg-purple-50",  dot: "bg-purple-500" },
  tokenized_deposit: { label: "Tokenized Settlement", text: "text-emerald-700", border: "border-emerald-500/40", bg: "bg-emerald-50", dot: "bg-emerald-500" },
};

const fmtUSD = (n, ccy = "USD") => {
  const v = Number(n || 0);
  if (ccy === "USD") return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${v.toLocaleString("en-US", { maximumFractionDigits: 2 })} ${ccy}`;
};

const maskIdentifier = (rec) => {
  if (!rec || !rec.identifier) return "—";
  const id = rec.identifier;
  const t = (rec.type || "").toLowerCase();
  if (t === "phone" || (id.startsWith("+") && /^\+\d+$/.test(id))) {
    // +15551234567 → +1 555 ••• 1234
    const digits = id.replace(/\D/g, "");
    if (digits.length >= 10) {
      const cc = digits.slice(0, digits.length - 10);
      const tail = digits.slice(-4);
      const area = digits.slice(-10, -7);
      return `+${cc} ${area} ••• ${tail}`;
    }
    return id;
  }
  if (id.startsWith("0x")) return `${id.slice(0, 6)}…${id.slice(-4)}`;
  if (id.length > 12) return `${id.slice(0, 4)}…${id.slice(-4)}`;
  return id;
};

const identifierTypeLabel = (type) => ({
  phone: "Phone number",
  iban: "IBAN",
  email: "Email",
  on_chain: "Wallet address",
  crypto: "Wallet address",
  member: "Member account",
  self: "Internal (own wallet)",
}[(type || "").toLowerCase()] || "Identifier");

const Section = ({ title, children, testId }) => (
  <section className="border-t border-slate-100 px-6 py-5" data-testid={testId}>
    <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">{title}</h3>
    {children}
  </section>
);

const KV = ({ k, v, mono = false, testId }) => (
  <div className="flex items-baseline justify-between gap-4 py-1">
    <span className="text-xs text-slate-500">{k}</span>
    <span className={`text-sm text-slate-900 text-right ${mono ? "font-mono text-xs" : ""}`} data-testid={testId}>{v}</span>
  </div>
);

const CopyBtn = ({ text }) => {
  const [done, setDone] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch (_e) { /* noop */ }
  };
  return (
    <button onClick={onCopy} aria-label="Copy"
      className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-700 transition"
      data-testid="routing-detail-copy-btn">
      {done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

export const RoutingDecisionDetail = ({ decisionId, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const close = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  useEffect(() => {
    if (!decisionId) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/v2/router-decisions/${decisionId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 403 ? "You don't have access to this routing decision." : "Failed to load decision."))
      .finally(() => setLoading(false));
  }, [decisionId]);

  if (!decisionId) return null;

  const rail = (data?.chosen_rail || "").toLowerCase();
  const style = RAIL_STYLE[rail] || { label: data?.rail_display || "Smart Router", text: "text-slate-700", border: "border-slate-300", bg: "bg-slate-50", dot: "bg-slate-500" };
  const status = data?.status || "—";
  const statusPill = {
    completed: "bg-emerald-100 text-emerald-700",
    pending:   "bg-amber-100 text-amber-700",
    failed:    "bg-rose-100 text-rose-700",
  }[(status || "").toLowerCase()] || "bg-slate-100 text-slate-700";

  const eligible = Array.isArray(data?.eligible_rails) ? data.eligible_rails : [];
  const policyApproved = Array.isArray(data?.policy_approved_rails) ? data.policy_approved_rails : [];
  const chosenRailKey = rail;

  // Compose the union of considered rails (eligible + chosen, dedup by rail key)
  const considered = (() => {
    const seen = new Map();
    eligible.forEach((r) => seen.set(r.rail, r));
    policyApproved.forEach((r) => seen.set(r.rail, { ...seen.get(r.rail), ...r }));
    if (chosenRailKey && !seen.has(chosenRailKey) && data?.selected_rail) {
      seen.set(chosenRailKey, data.selected_rail);
    }
    return [...seen.values()];
  })();

  // Compliance: derive flags from compliance_flags array if present
  const complianceFlags = Array.isArray(data?.compliance_flags) ? data.compliance_flags : [];

  // Timing trace from decision timestamp + executed_transaction
  const t0 = data?.timestamp ? new Date(data.timestamp).getTime() : null;
  const tExec = data?.executed_transaction?.timestamp ? new Date(data.executed_transaction.timestamp).getTime() : null;
  const settlementMs = data?.executed_transaction?.settlement_time_ms;
  const totalMs = (t0 != null && tExec != null) ? Math.max(0, tExec - t0) + (settlementMs || 0) : null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={close}
      role="dialog"
      aria-modal="true"
      data-testid="routing-detail-modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="routing-detail-modal"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-5 flex items-start gap-4 sticky top-0 bg-white rounded-t-2xl z-10 border-b border-slate-100">
          <div className="flex-1 min-w-0">
            {data && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border ${style.border} ring-1 ring-inset ${style.border}`} data-testid="routing-detail-rail-badge">
                    <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                    <span className={`text-[11px] font-bold uppercase tracking-wide ${style.text}`}>{style.label}</span>
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${statusPill}`} data-testid="routing-detail-status">{status}</span>
                </div>
                <p className="text-3xl font-bold text-slate-900 tracking-tight" data-testid="routing-detail-amount">
                  {fmtUSD(data.amount, data.source_currency || "USD")}
                  {data.target_currency && data.target_currency !== data.source_currency && (
                    <span className="text-sm text-slate-400 font-normal ml-2">→ {data.target_currency}</span>
                  )}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {data.timestamp ? new Date(data.timestamp).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "long" }) : "—"}
                </p>
                {data.reference_number && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Reference</span>
                    <code className="font-mono text-xs text-slate-600 bg-slate-50 px-2 py-0.5 rounded truncate max-w-[300px]" data-testid="routing-detail-reference">{data.reference_number}</code>
                    <CopyBtn text={data.reference_number} />
                  </div>
                )}
              </>
            )}
          </div>
          <button onClick={close} className="text-slate-400 hover:text-slate-700 transition p-1" aria-label="Close" data-testid="routing-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        )}

        {error && (
          <div className="px-6 py-12 text-center" data-testid="routing-detail-error">
            <AlertCircle className="w-8 h-8 mx-auto text-rose-500 mb-2" />
            <p className="text-sm text-slate-700">{error}</p>
          </div>
        )}

        {data && !loading && !error && (
          <>
            {/* Section 1 — Recipient */}
            <Section title="Recipient (resolved)" testId="routing-detail-section-recipient">
              <div className="space-y-1">
                <KV k="Type" v={identifierTypeLabel(data.resolved_recipient?.type)} testId="routing-detail-recipient-type" />
                <KV k="Identifier" v={maskIdentifier(data.resolved_recipient)} mono testId="routing-detail-recipient-id" />
                {data.resolved_recipient?.name && <KV k="Name" v={data.resolved_recipient.name} />}
                {data.resolved_recipient?.country && <KV k="Country" v={data.resolved_recipient.country} />}
                <KV k="KYC status" v={
                  <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                    data.resolved_recipient?.kyc_status === "verified" ? "bg-emerald-100 text-emerald-700" :
                    data.resolved_recipient?.kyc_status === "pending"  ? "bg-amber-100 text-amber-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>{data.resolved_recipient?.kyc_status || "unknown"}</span>
                } testId="routing-detail-recipient-kyc" />
                {data.resolved_recipient?.member_id && <KV k="Member ID" v={data.resolved_recipient.member_id} mono />}
              </div>
            </Section>

            {/* Section 2 — Examiner Rationale */}
            <Section title="Routing Rationale (examiner view)" testId="routing-detail-section-rationale">
              <p className="text-xs text-slate-500 mb-2">Full audit rationale (examiner view)</p>
              <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words border border-slate-200" data-testid="routing-detail-rationale">{data.rationale || "—"}</pre>
            </Section>

            {/* Section 3 — Rails Considered */}
            <Section title="Rails Considered" testId="routing-detail-section-rails">
              {considered.length === 0 ? (
                <p className="text-sm text-slate-400 italic">Only one rail evaluated for this decision.</p>
              ) : (
                <div className="space-y-2">
                  {considered.map((r) => {
                    const rs = RAIL_STYLE[r.rail] || RAIL_STYLE.fednow;
                    const isChosen = r.rail === chosenRailKey;
                    return (
                      <div key={r.rail} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${isChosen ? `${rs.border} ${rs.bg}` : "border-slate-200 bg-white"}`} data-testid={`routing-detail-rail-${r.rail}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`w-2 h-2 rounded-full ${rs.dot} flex-shrink-0`} />
                          <span className={`text-sm font-semibold ${rs.text}`}>{rs.label}</span>
                          {isChosen ? (
                            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Selected ✓</span>
                          ) : (
                            <span className="text-[10px] text-slate-400">Not selected — {r.reason || "lower priority"}</span>
                          )}
                        </div>
                        <div className="text-right text-xs text-slate-500 flex-shrink-0">
                          <span className="font-mono">${(r.estimated_fee ?? 0).toFixed(2)}</span>
                          <span className="mx-2">·</span>
                          <span>{r.estimated_settlement_seconds ?? "?"}s</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>

            {/* Section 4 — Compliance */}
            <Section title="Compliance" testId="routing-detail-section-compliance">
              {complianceFlags.length === 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <ShieldCheck className="w-4 h-4 text-emerald-500" />
                    <span>No compliance flags raised.</span>
                  </div>
                  <KV k="OFAC check" v={
                    <span className="inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Pass</span>
                  } />
                  <KV k="Travel Rule" v={
                    <span className="text-slate-600">{(data.amount || 0) > 3000 && data.target_currency !== "USD" ? "Required (data collected)" : "Not applicable"}</span>
                  } />
                  <KV k="Sanctions screening" v={data.timestamp ? new Date(data.timestamp).toLocaleString() : "—"} />
                </div>
              ) : (
                <div className="space-y-2">
                  {complianceFlags.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid={`routing-detail-compliance-flag-${i}`}>
                      <AlertCircle className="w-4 h-4" />
                      <span>{typeof f === "string" ? f : f.detail || JSON.stringify(f)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Section 5 — Timing trace */}
            <Section title="Timing Trace" testId="routing-detail-section-timing">
              <div className="flex items-center gap-1 overflow-x-auto pb-2">
                {[
                  { label: "Initiated", ts: t0 },
                  { label: "Executed", ts: tExec },
                  { label: "Confirmed", ts: tExec != null && settlementMs != null ? tExec + settlementMs : tExec },
                ].map((step, idx, arr) => (
                  <div key={idx} className="flex items-center flex-shrink-0">
                    <div className="text-center">
                      <div className="w-2 h-2 rounded-full bg-[#007377] mx-auto" />
                      <p className="text-[10px] font-semibold text-slate-700 mt-1">{step.label}</p>
                      <p className="text-[10px] text-slate-400 font-mono">
                        {step.ts ? `+${idx === 0 ? 0 : Math.max(0, step.ts - t0)}ms` : "—"}
                      </p>
                    </div>
                    {idx < arr.length - 1 && <div className="w-12 h-px bg-slate-200 mx-2" />}
                  </div>
                ))}
              </div>
              <div className="mt-3 text-sm">
                <span className="text-slate-500">Total duration:</span>{" "}
                <span className="font-bold text-slate-900" data-testid="routing-detail-total-duration">
                  {totalMs != null ? `${totalMs}ms` : "—"}
                </span>
                {totalMs != null && totalMs < 100 && (
                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Sub-100ms</span>
                )}
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
};
